import React, { useState } from 'react';
import { Box, ButtonBase, Chip, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { confirmTrade, skipTrade } from '../../trades/tradesSlice';
import { buildConfirmOverrides, CONFIRM_REASONS, getSkipReasonLabels } from '../../trades/reviewHelpers';
import SkipTradeDialog from '../../trades/SkipTradeDialog';
import { showNotification } from '../../notifications/notificationsSlice';
import { num, signalReasons } from './signalReasons';
import { computeRiskReward } from '../../trades/reviewHelpers';

// One planned trade in Today's decision queue (DWLF-302; canvas board AppBrief). Confirm / Resize /
// Skip decide it in place — through the same thunks, reasons and validation as the Review page, so a
// decision made here records exactly what one made there does. The full Review page stays a link away
// for entry-price and entry-time overrides.

const tok = (t) => `var(--dwlf-${t})`;
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.12em' };
const MONO = '"IBM Plex Mono", monospace';

// num(): null/'' are absent, not 0 — a missing price must not show as "0".
const fmt = (v) => (num(v) === null ? '—' : num(v).toLocaleString('en-GB', { maximumFractionDigits: num(v) >= 100 ? 2 : 4 }));
const actionSx = (primary) => ({
  ...LABEL, height: 44, px: 2, fontSize: '0.6875rem',
  backgroundColor: primary ? tok('accent') : 'transparent',
  color: primary ? tok('on-accent') : tok('text'),
  border: primary ? 'none' : `1px solid ${tok('ink-20')}`,
  '&:hover': primary ? { backgroundColor: tok('accent-hi') } : { borderColor: tok('accent'), color: tok('accent') },
  '&.Mui-disabled': { opacity: 0.5 },
});

// The inline decide step: optional size (Resize), confirm reasons and a note — what the Review page asks.
function ConfirmPanel({ mode, planned, size, setSize, reasons, toggleReason, note, setNote, error, submitting, onConfirm, onCancel }) {
  return (
    <Box sx={{ borderTop: `1px solid ${tok('ink-08')}`, pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {mode === 'resize' && (
        <TextField
          label={`Position size (planned ${Number.isFinite(planned) ? planned : '—'})`}
          value={size}
          onChange={(e) => setSize(e.target.value)}
          size="small"
          inputProps={{ inputMode: 'numeric' }}
          sx={{ maxWidth: 260 }}
        />
      )}
      <Typography component="span" sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-55') }}>Why are you taking it? (optional, feeds the scorecard)</Typography>
      <Box role="group" aria-label="Confirm reasons" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {CONFIRM_REASONS.map((r) => {
          const on = reasons.includes(r.value);
          return (
            <ButtonBase key={r.value} aria-pressed={on} onClick={() => toggleReason(r.value)} title={r.label}
              sx={{ fontFamily: MONO, fontSize: '0.75rem', px: 1.25, minHeight: 32, textAlign: 'left',
                border: `1px solid ${on ? tok('accent') : tok('ink-15')}`, backgroundColor: on ? tok('accent-10') : 'transparent', color: on ? tok('accent') : tok('ink-70') }}>
              {r.label.split(' — ')[0]}
            </ButtonBase>
          );
        })}
      </Box>
      <TextField label="Note" value={note} onChange={(e) => setNote(e.target.value)} size="small" multiline minRows={1} />
      {error && <Typography role="alert" sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok('down') }}>{error}</Typography>}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <ButtonBase disabled={submitting} onClick={onConfirm} sx={actionSx(true)}>
          {mode === 'resize' ? 'Confirm at this size' : 'Confirm trade'}
        </ButtonBase>
        <ButtonBase onClick={onCancel} sx={{ ...LABEL, fontSize: '0.625rem', px: 1.5, height: 44, color: tok('ink-55') }}>Cancel</ButtonBase>
      </Box>
    </Box>
  );
}

export default function DecisionCard({ trade, onDecided }) {
  const dispatch = useDispatch();
  const [mode, setMode] = useState(null); // null | 'confirm' | 'resize'
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState('');
  const [size, setSize] = useState('');
  const [skipOpen, setSkipOpen] = useState(false);
  const [error, setError] = useState('');
  const [skipError, setSkipError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const rr = computeRiskReward(trade.entryPrice, trade.initialStop, trade.initialTakeProfit);
  const planned = num(trade.positionSize);
  const open = (next) => {
    setError('');
    setMode((m) => (m === next ? null : next));
    // Suggest half the plan only when that is a whole number of units below it (the confirm endpoint
    // takes whole units). Otherwise leave it blank: a suggestion must never exceed the plan.
    if (next === 'resize' && size === '' && Number.isInteger(planned) && planned >= 2) setSize(String(Math.floor(planned / 2)));
  };
  const toggleReason = (v) => setReasons((r) => (r.includes(v) ? r.filter((x) => x !== v) : [...r, v]));

  const handleStateChanged = (payload, fallback, setErr) => {
    if (payload.code === 'INVALID_STATE') {
      dispatch(showNotification({ message: 'Trade state changed — refreshing', severity: 'warning' }));
      onDecided(trade.tradeId);
      return;
    }
    setErr(payload.message || fallback);
  };

  const confirm = async () => {
    setError('');
    // Resize means a different size: a blank or unchanged field would confirm the full plan unnoticed.
    if (mode === 'resize') {
      const n = Number(size);
      if (size.trim() === '' || !Number.isFinite(n) || n <= 0) { setError('Enter the size to take.'); return; }
      if (n === planned) { setError("That's the planned size. Use Confirm to take it as planned."); return; }
    }
    const result = buildConfirmOverrides({
      overrideEntryPrice: '', plannedEntryPrice: trade.entryPrice,
      overrideEntryAt: '', seedEntryAt: '',
      overridePositionSize: mode === 'resize' ? size : '', plannedPositionSize: trade.positionSize,
    });
    if (result.error) { setError(result.error.message); return; }
    const body = result.body;
    if (reasons.length) {
      if (reasons.includes('other') && !note.trim()) { setError('Describe the reason — "other" requires a note.'); return; }
      body.confirmReasons = reasons;
    }
    if (note.trim()) body.confirmNote = note.trim();

    setSubmitting(true);
    try {
      const action = await dispatch(confirmTrade({ tradeId: trade.tradeId, body }));
      if (confirmTrade.fulfilled.match(action)) {
        dispatch(showNotification({ message: `${trade.assetSymbol} confirmed${body.positionSize ? ` at ${body.positionSize}` : ''}`, severity: 'success' }));
        onDecided(trade.tradeId, { kind: body.positionSize ? 'Resized' : 'Confirmed', trade, reasons });
        return;
      }
      handleStateChanged(action.payload || {}, 'Could not confirm trade.', setError);
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async ({ skipReasons, skipNote }) => {
    setSkipError('');
    setSubmitting(true);
    try {
      const action = await dispatch(skipTrade({ tradeId: trade.tradeId, body: { skipReasons, ...(skipNote ? { skipNote } : {}) } }));
      if (skipTrade.fulfilled.match(action)) {
        dispatch(showNotification({ message: `${trade.assetSymbol} skipped — ${getSkipReasonLabels(skipReasons).join(', ')}`, severity: 'info' }));
        setSkipOpen(false);
        onDecided(trade.tradeId, { kind: 'Skipped', trade, reasons: skipReasons });
        return;
      }
      handleStateChanged(action.payload || {}, 'Could not skip trade.', setSkipError);
    } finally {
      setSubmitting(false);
    }
  };

  const why = signalReasons(trade);

  return (
    <Box component="article" aria-label={`${trade.assetSymbol} ${trade.direction} awaiting decision`}
      sx={{ border: `1px solid ${mode ? tok('accent') : tok('ink-12')}`, backgroundColor: tok('surface'), p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em', color: tok('text') }}>{trade.assetSymbol}</Typography>
          <Chip label={trade.direction} size="small" color={trade.direction === 'long' ? 'success' : 'error'} sx={{ textTransform: 'capitalize', fontWeight: 600 }} />
        </Box>
        <Typography component="span" sx={{ ...LABEL, fontSize: '0.625rem', color: tok('accent') }}>Awaiting your decision</Typography>
      </Box>

      {why.length > 0 && (
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 2, rowGap: 0.75, alignItems: 'baseline' }}>
          {why.map((w) => (
            <React.Fragment key={`${w.label}-${w.text}`}>
              <Box component="dt" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('ink-50'), m: 0 }}>{w.label || 'Why'}</Box>
              <Box component="dd" sx={{ m: 0, fontFamily: MONO, fontSize: '0.8125rem', lineHeight: 1.5, color: tok(w.label === 'Strategy' ? 'text' : 'ink-75') }}>{w.text}</Box>
            </React.Fragment>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '1px', backgroundColor: tok('ink-08') }}>
        {[
          { k: 'Entry', v: fmt(trade.entryPrice), c: 'text' },
          { k: 'Stop', v: fmt(trade.initialStop), c: 'down' },
          { k: 'Target', v: fmt(trade.initialTakeProfit), c: 'up' },
          { k: 'R:R', v: rr ? `${rr.toFixed(1)}:1` : '—', c: 'text' },
        ].map((s) => (
          <Box key={s.k} sx={{ backgroundColor: tok('surface'), p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography component="span" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('ink-50') }}>{s.k}</Typography>
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.875rem', color: tok(s.c) }}>{s.v}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.4fr 1fr 1fr' }, gap: 1 }}>
        <ButtonBase disabled={submitting} aria-expanded={mode === 'confirm'} onClick={() => open('confirm')} sx={actionSx(true)}>Confirm</ButtonBase>
        <ButtonBase disabled={submitting} aria-expanded={mode === 'resize'} onClick={() => open('resize')} sx={actionSx(false)}>Resize</ButtonBase>
        <ButtonBase disabled={submitting} onClick={() => setSkipOpen(true)} sx={actionSx(false)}>Skip</ButtonBase>
      </Box>

      {mode && (
        <ConfirmPanel
          mode={mode} planned={planned} size={size} setSize={setSize} reasons={reasons} toggleReason={toggleReason}
          note={note} setNote={setNote} error={error} submitting={submitting} onConfirm={confirm} onCancel={() => setMode(null)}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography component={RouterLink} to={`/markets/${String(trade.assetSymbol || '').replace('/', '-')}`} sx={{ ...LABEL, fontSize: '0.625rem', color: tok('accent'), textDecoration: 'none' }}>
          Open chart →
        </Typography>
        <Typography component={RouterLink} to={`/trades/${trade.tradeId}/review`} sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-55'), textDecoration: 'none' }}>
          Full review (entry overrides) →
        </Typography>
      </Box>

      <SkipTradeDialog open={skipOpen} onClose={() => setSkipOpen(false)} onConfirm={skip} submitting={submitting} errorMessage={skipError} />
    </Box>
  );
}
